import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  TextField,
  Button,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LogoutIcon from '@mui/icons-material/Logout';
import ShareIcon from '@mui/icons-material/Share';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton, FacebookIcon, TwitterIcon, WhatsappIcon } from 'react-share';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Navbar from './common/Navbar';
import Footer from './common/Footer';
import Breadcrumb from './common/Breadcrumb';
import Loader from './common/Loader';
import { useAuth } from '../context/AuthContext';
import { getUserAddress, addAddress, updateAddress, deleteAddress, setDefaultAddress } from '../services/api';
import { toast } from 'react-toastify';
import api from '../services/api';

const Profile = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [userInfo, setUserInfo] = useState(null);
  const [refData, setRefData] = useState([]);
  const [refDialog, setRefDialog] = useState(false);
  const [copyStatus, setCopyStatus] = useState(false);
  const [addressDialog, setAddressDialog] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({ label: 'Home', address: '', city: '', state: '', pinCode: '' });
  console.log("copyStatus",copyStatus);
  const onCopyText = () => {
    setCopyStatus(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopyStatus(false), 2000);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    const fetchUserData = async () => {
      try {
        const userId = localStorage.getItem('UserId');
        const response = await getUserAddress(userId);
        if (response.data.data && response.data.data[0]) {
          setUserInfo(response.data.data[0]);

          // Fetch reference data
          if (response.data.data[0].ReferenceID) {
            const refRes = await api.get(`/getReference/${response.data.data[0].ReferenceID}`);
            if (refRes.data) {
              setRefData(refRes.data.data || []);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleOpenAddressDialog = (addr = null) => {
    if (addr) {
      setEditingAddress(addr);
      setAddressForm({ label: addr.label, address: addr.address, city: addr.city, state: addr.state, pinCode: addr.pinCode });
    } else {
      setEditingAddress(null);
      setAddressForm({ label: 'Home', address: '', city: '', state: '', pinCode: '' });
    }
    setAddressDialog(true);
  };

  const handleSaveAddress = async () => {
    const userId = localStorage.getItem('UserId');
    try {
      if (editingAddress) {
        const res = await updateAddress(userId, editingAddress._id, addressForm);
        if (res.data.data) setUserInfo({ ...userInfo, Addresses: res.data.data });
        toast.success('Address updated');
      } else {
        const res = await addAddress(userId, addressForm);
        if (res.data.data) setUserInfo({ ...userInfo, Addresses: res.data.data });
        toast.success('Address added');
      }
      setAddressDialog(false);
    } catch (error) {
      toast.error('Failed to save address');
    }
  };

  const handleDeleteAddress = async (addressId) => {
    const userId = localStorage.getItem('UserId');
    try {
      const res = await deleteAddress(userId, addressId);
      if (res.data.data) setUserInfo({ ...userInfo, Addresses: res.data.data });
      toast.success('Address deleted');
    } catch (error) {
      toast.error('Failed to delete address');
    }
  };

  const handleSetDefault = async (addressId) => {
    const userId = localStorage.getItem('UserId');
    try {
      const res = await setDefaultAddress(userId, addressId);
      if (res.data.data) setUserInfo({ ...userInfo, Addresses: res.data.data });
      toast.success('Default address updated');
    } catch (error) {
      toast.error('Failed to set default address');
    }
  };

  const menuItems = [
    { id: 'profile', label: 'My Profile', icon: <PersonIcon /> },
    { id: 'orders', label: 'My Orders', icon: <ShoppingBagIcon />, path: '/orders' },
    { id: 'wishlist', label: 'Wishlist', icon: <FavoriteIcon />, path: '/wishlist' },
    { id: 'addresses', label: 'Addresses', icon: <LocationOnIcon /> },
    { id: 'referrals', label: 'Referrals', icon: <ShareIcon /> },
  ];

  if (loading) {
    return <Loader message="Loading profile..." />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#faf8f5' }}>
      <Navbar />

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Breadcrumb items={[{ label: 'My Profile' }]} />

        <Typography variant="h4" fontWeight="bold" color="#3E2723" sx={{ mb: 3 }}>
          My Account
        </Typography>

        <Grid container spacing={4}>
          {/* Sidebar */}
          <Grid item xs={12} md={3}>
            <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: '#fff', overflow: 'hidden' }}>
              {/* User Info */}
              <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#8B4513', color: '#fff' }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    bgcolor: '#fff',
                    color: '#8B4513',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                  }}
                >
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
                <Typography variant="h6" fontWeight="bold">
                  {userInfo?.FirstName} {userInfo?.LastName}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {userInfo?.Email}
                </Typography>
              </Box>

              {/* Menu */}
              <List>
                {menuItems.map((item) => (
                  <ListItemButton
                    key={item.id}
                    selected={activeTab === item.id}
                    onClick={() => {
                      if (item.path) {
                        navigate(item.path);
                      } else {
                        setActiveTab(item.id);
                      }
                    }}
                    sx={{
                      '&.Mui-selected': {
                        bgcolor: '#faf8f5',
                        borderLeft: '4px solid #8B4513',
                        '&:hover': { bgcolor: '#faf8f5' },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: activeTab === item.id ? '#8B4513' : 'inherit' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
                <Divider />
                <ListItemButton onClick={handleLogout} sx={{ color: '#e53935' }}>
                  <ListItemIcon sx={{ color: '#e53935' }}>
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText primary="Logout" />
                </ListItemButton>
              </List>
            </Paper>
          </Grid>

          {/* Content */}
          <Grid item xs={12} md={9}>
            {activeTab === 'profile' && (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#fff' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Personal Information
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={userInfo?.FirstName || ''}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={userInfo?.LastName || ''}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={userInfo?.Email || ''}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Mobile"
                      value={userInfo?.Mobile || ''}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Gender"
                      value={userInfo?.Gender || ''}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </Grid>

                <Button
                  variant="contained"
                  sx={{
                    mt: 3,
                    bgcolor: '#8B4513',
                    '&:hover': { bgcolor: '#6d3610' },
                  }}
                >
                  Edit Profile
                </Button>
              </Paper>
            )}

            {activeTab === 'addresses' && (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#fff' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    Saved Addresses
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenAddressDialog()}
                    sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#6d3610' } }}
                  >
                    Add New Address
                  </Button>
                </Box>
                <Divider sx={{ mb: 3 }} />

                {userInfo?.Addresses && userInfo.Addresses.length > 0 ? (
                  <Grid container spacing={2}>
                    {userInfo.Addresses.map((addr) => (
                      <Grid item xs={12} sm={6} key={addr._id}>
                        <Box
                          sx={{
                            p: 2,
                            border: addr.isDefault ? '2px solid #8B4513' : '1px solid #ddd',
                            borderRadius: 2,
                            bgcolor: addr.isDefault ? '#faf8f5' : '#fff',
                            position: 'relative',
                          }}
                        >
                          {addr.isDefault && (
                            <Chip label="Default" size="small" sx={{ bgcolor: '#8B4513', color: '#fff', position: 'absolute', top: 8, right: 8 }} />
                          )}
                          <Typography fontWeight="bold" gutterBottom>{addr.label}</Typography>
                          <Typography variant="body2" color="text.secondary">{addr.address}</Typography>
                          <Typography variant="body2" color="text.secondary">{addr.city}, {addr.state}</Typography>
                          <Typography variant="body2" color="text.secondary">Pin: {addr.pinCode}</Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                            <IconButton size="small" onClick={() => handleOpenAddressDialog(addr)} sx={{ bgcolor: '#e3f2fd' }}>
                              <EditIcon fontSize="small" sx={{ color: '#1976d2' }} />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteAddress(addr._id)} sx={{ bgcolor: '#ffebee' }}>
                              <DeleteIcon fontSize="small" sx={{ color: '#d32f2f' }} />
                            </IconButton>
                            {!addr.isDefault && (
                              <Button size="small" startIcon={<CheckCircleIcon />} onClick={() => handleSetDefault(addr._id)} sx={{ color: '#8B4513', ml: 'auto' }}>
                                Set Default
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <LocationOnIcon sx={{ fontSize: 60, color: '#ddd' }} />
                    <Typography color="text.secondary" sx={{ mt: 2 }}>
                      No saved addresses. Add your first address!
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}

            {activeTab === 'referrals' && (
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#fff' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    Your Referral Program
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<ShareIcon />}
                    onClick={() => setRefDialog(true)}
                    sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#6d3610' } }}
                  >
                    Share & Earn
                  </Button>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Box sx={{ bgcolor: '#faf8f5', p: 3, borderRadius: 2, mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Your Referral Code
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h5" fontWeight="bold" color="#8B4513">
                      {userInfo?.ReferenceID || 'N/A'}
                    </Typography>
                    <CopyToClipboard text={userInfo?.ReferenceID} onCopy={onCopyText}>
                      <IconButton size="small">
                        <ContentCopyIcon />
                      </IconButton>
                    </CopyToClipboard>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Share this code with friends and earn rewards when they sign up
                  </Typography>
                </Box>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Your Referrals ({refData.length})
                </Typography>

                {refData.length > 0 ? (
                  <List>
                    {refData.map((ref, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          mb: 1,
                          p: 2,
                          border: '1px solid #eee',
                          borderRadius: 2,
                        }}
                      >
                        <Avatar sx={{ bgcolor: '#8B4513', mr: 2 }}>
                          {ref.FirstName?.charAt(0)}
                        </Avatar>
                        <ListItemText
                          primary={`${ref.FirstName} ${ref.LastName}`}
                          secondary={ref.Email}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <ShareIcon sx={{ fontSize: 60, color: '#ddd' }} />
                    <Typography color="text.secondary" sx={{ mt: 2 }}>
                      No referrals yet. Share your code to start earning!
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Referral Dialog */}
      <Dialog open={refDialog} onClose={() => setRefDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Share Your Referral Code
          <IconButton
            onClick={() => setRefDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" fontWeight="bold" color="#8B4513" gutterBottom>
              {userInfo?.ReferenceID}
            </Typography>
            <CopyToClipboard text={userInfo?.ReferenceID} onCopy={onCopyText}>
              <Button startIcon={<ContentCopyIcon />} sx={{ mb: 3 }}>
                Copy Code
              </Button>
            </CopyToClipboard>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              Share on social media:
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
              <FacebookShareButton
                url={`https://nutnutritions.com/?popupsignup=true&ref=${userInfo?.ReferenceID}`}
                quote={`Use my referral code ${userInfo?.ReferenceID} to get exclusive discounts!`}
              >
                <FacebookIcon size={40} round />
              </FacebookShareButton>
              <TwitterShareButton
                url={`https://nutnutritions.com/?popupsignup=true&ref=${userInfo?.ReferenceID}`}
                title={`Use my referral code ${userInfo?.ReferenceID} to get exclusive discounts!`}
              >
                <TwitterIcon size={40} round />
              </TwitterShareButton>
              <WhatsappShareButton
                url={`https://nutnutritions.com/?popupsignup=true&ref=${userInfo?.ReferenceID}`}
                title={`Use my referral code ${userInfo?.ReferenceID} to get exclusive discounts!`}
              >
                <WhatsappIcon size={40} round />
              </WhatsappShareButton>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Address Dialog */}
      <Dialog open={addressDialog} onClose={() => setAddressDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAddress ? 'Edit Address' : 'Add New Address'}
          <IconButton onClick={() => setAddressDialog(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Label (e.g. Home, Office)" value={addressForm.label} onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })} fullWidth size="small" />
            <TextField label="Address" value={addressForm.address} onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })} fullWidth size="small" />
            <TextField label="City" value={addressForm.city} onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })} fullWidth size="small" />
            <TextField label="State" value={addressForm.state} onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })} fullWidth size="small" />
            <TextField label="Pin Code" value={addressForm.pinCode} onChange={(e) => setAddressForm({ ...addressForm, pinCode: e.target.value })} fullWidth size="small" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddressDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAddress} sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#6d3610' } }}>
            {editingAddress ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </Box>
  );
};

export default Profile;
